import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

// Analytics API hooks
export const useModelUsageByPurpose = (dateRange?: { startDate?: string; endDate?: string }) => {
  return useQuery({
    queryKey: ["/api/admin/analytics/model-usage/purpose", dateRange],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.set("startDate", dateRange.startDate);
      if (dateRange?.endDate) params.set("endDate", dateRange.endDate);
      
      const response = await apiRequest(`/api/admin/analytics/model-usage/purpose?${params}`, "GET");
      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useModelUsageByPlan = (dateRange?: { startDate?: string; endDate?: string }) => {
  return useQuery({
    queryKey: ["/api/admin/analytics/model-usage/plans", dateRange],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.set("startDate", dateRange.startDate);
      if (dateRange?.endDate) params.set("endDate", dateRange.endDate);
      
      const response = await apiRequest(`/api/admin/analytics/model-usage/plans?${params}`, "GET");
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useModelCostAnalysis = (dateRange?: { startDate?: string; endDate?: string }) => {
  return useQuery({
    queryKey: ["/api/admin/analytics/model-cost-analysis", dateRange],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.set("startDate", dateRange.startDate);
      if (dateRange?.endDate) params.set("endDate", dateRange.endDate);
      
      const response = await apiRequest(`/api/admin/analytics/model-cost-analysis?${params}`, "GET");
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useRevenueAnalytics = (dateRange?: { startDate?: string; endDate?: string }) => {
  return useQuery({
    queryKey: ["/api/admin/analytics/revenue", dateRange],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.set("startDate", dateRange.startDate);
      if (dateRange?.endDate) params.set("endDate", dateRange.endDate);
      
      const response = await apiRequest(`/api/admin/analytics/revenue?${params}`, "GET");
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useCouponAnalytics = (dateRange?: { startDate?: string; endDate?: string }) => {
  return useQuery({
    queryKey: ["/api/admin/analytics/coupons", dateRange],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.set("startDate", dateRange.startDate);
      if (dateRange?.endDate) params.set("endDate", dateRange.endDate);
      
      const response = await apiRequest(`/api/admin/analytics/coupons?${params}`, "GET");
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useReferralAnalytics = (dateRange?: { startDate?: string; endDate?: string }) => {
  return useQuery({
    queryKey: ["/api/admin/analytics/referrals", dateRange],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.set("startDate", dateRange.startDate);
      if (dateRange?.endDate) params.set("endDate", dateRange.endDate);
      
      const response = await apiRequest(`/api/admin/analytics/referrals?${params}`, "GET");
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useModelPerformanceMetrics = (dateRange?: { startDate?: string; endDate?: string }) => {
  return useQuery({
    queryKey: ["/api/admin/analytics/model-performance", dateRange],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.set("startDate", dateRange.startDate);
      if (dateRange?.endDate) params.set("endDate", dateRange.endDate);
      
      const response = await apiRequest(`/api/admin/analytics/model-performance?${params}`, "GET");
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useAdminDashboardSummary = () => {
  return useQuery({
    queryKey: ["/api/admin/dashboard/summary"],
    queryFn: async () => {
      const response = await apiRequest("/api/admin/dashboard/summary", "GET");
      return response.json();
    },
    staleTime: 2 * 60 * 1000, // 2 minutes for dashboard
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });
};

export const useUserAnalytics = (filters?: any) => {
  return useQuery({
    queryKey: ["/api/admin/users/analytics", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters) {
        Object.keys(filters).forEach(key => {
          if (filters[key]) params.set(key, filters[key]);
        });
      }
      
      const response = await apiRequest(`/api/admin/users/analytics?${params}`, "GET");
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
  });
};